import { BottomSheet } from "@povio/ui";
import clsx from "clsx";
import { useState } from "react";
import { Menu as AriaMenu, MenuTrigger as AriaMenuTrigger, Popover as AriaPopover } from "react-aria-components";
import useMeasure from "react-use-measure";

import type { MenuItem, MenuProps } from "@/components/ui/Menu/Menu";
import { MenuItem as MenuItemComponent } from "@/components/ui/Menu/MenuItem";
import { MenuList } from "@/components/ui/Menu/MenuList";

export const MenuMobile = ({
  trigger,
  items,
  triggerClassName,
  popoverProps,
  id,
  isOpen: controlledIsOpen,
  onOpenChange,
}: MenuProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<MenuItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [triggerRef, { width }] = useMeasure();

  // Use controlled state if provided, otherwise use internal state
  const isPopoverOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handlePopoverOpenChange = onOpenChange || setInternalIsOpen;

  const handleItemClick = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      // Item has children - close popover and open drawer
      handlePopoverOpenChange(false);
      setSelectedParent(item);
      setIsDrawerOpen(true);
    } else {
      // Item has no children - execute action and close popover
      item.onAction?.();
      handlePopoverOpenChange(false);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedParent(null);
  };

  const handleChildItemClick = (childItem: MenuItem) => {
    // Execute child action and close drawer
    childItem.onAction?.();
    handleDrawerClose();
  };

  return (
    <>
      <AriaMenuTrigger
        isOpen={isPopoverOpen}
        onOpenChange={handlePopoverOpenChange}
      >
        <div
          ref={triggerRef}
          className={clsx("w-auto flex-auto", triggerClassName)}
          id={id}
        >
          {trigger}
        </div>

        <AriaPopover
          {...popoverProps}
          offset={0}
          className={clsx("pt-2 outline-hidden", popoverProps?.className)}
          style={{ width: width || undefined }}
        >
          <div className="overflow-hidden rounded-s border border-secondary-300 bg-secondary-500 shadow-menu outline-hidden">
            <AriaMenu>
              {items.map((item) => (
                <MenuItemComponent
                  key={item.label}
                  {...item}
                  onAction={() => handleItemClick(item)}
                />
              ))}
            </AriaMenu>
          </div>
        </AriaPopover>
      </AriaMenuTrigger>

      {selectedParent && (
        <BottomSheet
          height="auto"
          isOpen={isDrawerOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              handleDrawerClose();
            }
          }}
          isDismissable
          aria-label="sheet"
        >
          <div className="flex max-h-[calc(100dvh-96px)] grow flex-col justify-end">
            <div className="flex max-h-[50dvh] w-full flex-col overflow-y-auto">
              <MenuList
                items={selectedParent.children || []}
                onItemClick={handleChildItemClick}
                className="bg-elevation-fill-default-1 text-start"
              />
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
};
